import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.mjs';
import FileDown from 'lucide-react/dist/esm/icons/file-down.mjs';
import LoaderCircle from 'lucide-react/dist/esm/icons/loader-circle.mjs';
import { AutoSaveContext } from './autoSaveContext.js';
import { GeneratedFiles } from './GeneratedFiles.js';
import { ExportFormatIcon } from './exportFormatIcons.js';
import { documentContent, documentContentIssue, documentOutputTitle } from './documentRequests.js';
const formats = [
    { format: 'pdf', label: 'PDF document' }, { format: 'docx', label: 'Word document' },
    { format: 'xlsx', label: 'Excel workbook' }, { format: 'pptx', label: 'PowerPoint slides' },
    { format: 'md', label: 'Markdown' }, { format: 'txt', label: 'Plain text' },
    { format: 'csv', label: 'CSV table' }, { format: 'tsv', label: 'TSV table' },
    { format: 'json', label: 'JSON data' }, { format: 'html', label: 'HTML document' },
    { format: 'zip', label: 'ZIP document bundle' }, { format: 'skill', label: 'Skill package' },
];
/** Menu groups: office files, prose, data, web, bundles. The flat `formats` order and labels are unchanged. */
const groups = [['pdf', 'docx', 'xlsx', 'pptx'], ['md', 'txt'], ['csv', 'tsv', 'json'], ['html'], ['zip', 'skill']];
const menuGroups = groups.map(group => formats.filter(item => group.includes(item.format)));
const EMPTY = [];
/** Said when a reply holds nothing but Gyro's own notes: no file is made from it. */
const NO_CONTENT = 'There is no document content to export yet.';
/**
 * A reply's files and its Export menu (the desktop app and the Office pane). `openPdf` and `pdfFallback` are "Create my
 * PPP"'s Open PDF chip and its durable fallback, handed to the file cards as they are (GeneratedFiles), so the chip works
 * here as it does under a plain file list. `attachedName` is the name of the file attached to the request, which names
 * an export when the request and the answer name none (documentOutputTitle). `webPages` is the side panel's web page
 * preview, handed to the cards too: a web page made here (Export > HTML) or by gyro-svc gets its Preview button.
 *
 * In the desktop app each file made here is copied to Documents\Gyro\<chat name> the moment it exists (owner, 2026-10-22;
 * desktopAutoSave.tsx), even when the chat has moved on before it was finished.
 */
export function DocumentOutputs({ replyId, question, attachedName, markdown, requested, autoCreate, artifacts = EMPTY, client, store, onFiles, openPdf, pdfFallback, webPages }) {
    const [local, setLocal] = useState(() => store.forReply(replyId));
    const [pending, setPending] = useState('');
    const [error, setError] = useState('');
    const [menu, setMenu] = useState(false);
    const [menuLayout, setMenuLayout] = useState({ below: false, height: 240 });
    const live = useRef(false);
    const running = useRef(false);
    const automatic = useRef(false);
    const onFilesRef = useRef(onFiles);
    onFilesRef.current = onFiles;
    const root = useRef(null);
    const trigger = useRef(null);
    const autoSave = useContext(AutoSaveContext);
    const all = [...artifacts, ...local];
    useLayoutEffect(() => {
        if (!menu)
            return;
        const place = () => {
            const button = trigger.current;
            if (!button)
                return;
            let top = 8, bottom = window.innerHeight - 8;
            for (let parent = button.parentElement; parent; parent = parent.parentElement) {
                if (!/auto|scroll|hidden|clip/.test(getComputedStyle(parent).overflowY))
                    continue;
                const bounds = parent.getBoundingClientRect();
                top = Math.max(top, bounds.top);
                bottom = Math.min(bottom, bounds.bottom);
            }
            // The absolutely positioned menu anchors to the control, whose line box differs from the button.
            const bounds = (button.parentElement ?? button).getBoundingClientRect();
            const above = bounds.top - top - 8, below = bottom - bounds.bottom - 8;
            const layout = { below: below > above, height: Math.max(40, Math.min(420, Math.max(above, below))) };
            setMenuLayout(previous => previous.below === layout.below && previous.height === layout.height ? previous : layout);
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
    }, [menu]);
    useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
    useEffect(() => {
        const retained = store.forReply(replyId);
        if (retained.length)
            onFilesRef.current([...artifacts, ...retained]);
        // Restored conversations also restore their task-panel downloads.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store, replyId]);
    useEffect(() => {
        if (!menu)
            return;
        const outside = (event) => { if (!root.current?.contains(event.target))
            setMenu(false); };
        document.addEventListener('pointerdown', outside);
        return () => document.removeEventListener('pointerdown', outside);
    }, [menu]);
    const create = async (choices, automaticRequest = false) => {
        if (running.current)
            return;
        setMenu(false);
        // Gyro's own notes (out of room, cut off, a file not saved …) are never a document's content: a reply that is only
        // notes makes no file, and one with content makes it without them (documentRequests.ts documentContent).
        const content = documentContent(markdown);
        const issue = !content ? NO_CONTENT : automaticRequest ? documentContentIssue(content) : null;
        if (issue) {
            setError(issue);
            return;
        }
        running.current = true;
        setError('');
        const failures = [];
        try {
            for (const format of choices) {
                if (!live.current)
                    break;
                if (automaticRequest && all.some(file => file.name.toLowerCase().endsWith(`.${format}`)))
                    continue;
                setPending(`Creating ${formats.find(item => item.format === format).label.toLowerCase()}…`);
                try {
                    const made = await store.create(replyId, { format, title: documentOutputTitle(question, content, attachedName), markdown: content });
                    // The copy is asked for before anything else, so a file finished after the chat moved on is still kept.
                    if (autoSave) {
                        try {
                            autoSave.saveLocal(made, store.resolve(made));
                        }
                        catch { /* the conversation closed: nothing left to copy */ }
                    }
                    if (!live.current)
                        return;
                    const files = store.forReply(replyId);
                    setLocal(files);
                    onFilesRef.current([...artifacts, ...files]);
                }
                catch (cause) {
                    failures.push(`${format.toUpperCase()}: ${cause instanceof Error ? cause.message : 'Could not create this file. Try Export again.'}`);
                }
            }
            if (live.current)
                setError(failures.join(' '));
        }
        finally {
            running.current = false;
            if (live.current)
                setPending('');
        }
    };
    // Only a successfully completed response from this session can trigger automatic creation.
    // No navigation, streamed partial response, stopped request, or question card can create a file.
    useEffect(() => {
        if (!autoCreate || automatic.current || !requested.length)
            return;
        automatic.current = true;
        void create(requested, true);
        // Mount is the completion boundary; callbacks stay fresh through onFilesRef.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoCreate]);
    return _jsxs("div", { className: "gyro-document-output", ref: root, children: [_jsx(GeneratedFiles, { artifacts: all, client: client, resolveLocal: store.resolve, openPdf: openPdf, pdfFallback: pdfFallback, webPages: webPages }), pending && _jsxs("p", { className: "gyro-document-progress", role: "status", children: [_jsx(LoaderCircle, { size: 15, className: "gyro-file-spinner" }), pending] }), error && _jsx("p", { className: "gyro-file-error", role: "alert", children: error }), _jsxs("div", { className: "gyro-export-control", onKeyDown: event => { if (event.key === 'Escape' && menu) {
                    event.preventDefault();
                    event.stopPropagation();
                    setMenu(false);
                    trigger.current?.focus();
                } }, children: [_jsxs("button", { ref: trigger, type: "button", className: "gyro-export-trigger", disabled: Boolean(pending), "aria-expanded": menu, onClick: () => setMenu(value => !value), children: [_jsx(FileDown, { size: 15 }), " Export ", _jsx(ChevronDown, { size: 14, className: "gyro-export-chevron" })] }), menu && _jsxs("div", { className: "gyro-export-menu", style: { maxHeight: menuLayout.height, ...(menuLayout.below ? { bottom: 'auto', top: 'calc(100% + 8px)' } : {}) }, role: "group", "aria-label": "Export this answer", children: [_jsxs("div", { className: "gyro-export-menu-head", children: [_jsx("strong", { children: "Export as" }), _jsx("span", { children: "Choose a file format" })] }), menuGroups.map(group => _jsx("div", { className: "gyro-export-group", children: group.map(item => _jsxs("button", { type: "button", "aria-label": item.label, onClick: () => void create([item.format]), children: [_jsx(ExportFormatIcon, { format: item.format }), _jsx("span", { className: "gyro-export-label", children: item.label }), _jsxs("small", { children: [".", item.format] })] }, item.format)) }, group[0].format))] })] }), local.length > 0 && _jsx("p", { className: "gyro-file-preview-note", children: autoSave ? 'Created on this computer.' : 'Created on this computer. Download to keep a copy before closing Gyro.' })] });
}
//# sourceMappingURL=DocumentOutputs.js.map